import React, { useState, useMemo } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';
export default function ProjectRentals({ project, updateProject, safeNavigate }) {
  const [newItem, setNewItem] = useState({ name: '', category: 'Furniture', quantity: '', vendor: '', status: 'Inquiry', cost: '' });
  
  const rentals = project.rentals || [];
  const vendors = project.vendors || [];

  const categories = ['Furniture', 'Linens', 'Tableware', 'Decor', 'Lighting', 'AV / Sound', 'Tenting', 'Other'];
  const statuses = ['Inquiry', 'Reserved', 'Confirmed', 'Delivered', 'Returned'];

  const stats = useMemo(() => {
    return {
      totalItems: rentals.length,
      confirmed: rentals.filter(r => r.status === 'Confirmed' || r.status === 'Delivered').length,
      totalCost: rentals.reduce((sum, r) => sum + (Number(r.cost) || 0), 0)
    };
  }, [rentals]);

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;

    updateProject(project.id, {
      rentals: [...rentals, { ...newItem, id: Date.now().toString() }]
    });
    
    setNewItem({ name: '', category: 'Furniture', quantity: '', vendor: '', status: 'Inquiry', cost: '' });
  };

  const handleRemoveItem = (id) => {
    if (window.confirm("Remove this rental item?")) {
      updateProject(project.id, {
        rentals: rentals.filter(item => item.id !== id)
      });
    }
  };

  const handleUpdateItem = (id, field, value) => {
    const updated = rentals.map(item => item.id === id ? { ...item, [field]: value } : item);
    updateProject(project.id, { rentals: updated });
  };

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in flex flex-col h-full">
        {/* Header & Stats */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-6">
                <div>
                    <h2 className="text-2xl font-bold font-serif text-gray-900">Rentals & Inventory</h2>
                    <p className="text-sm text-gray-500 mt-1">Track furniture, linens, and equipment rentals across all vendors.</p>
                </div>
                <div className="bg-blue-50 px-4 py-3 rounded-xl border border-blue-100 flex-1 sm:flex-none text-center sm:text-left">
                    <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">Total Rental Cost</p>
                    <p className="text-2xl font-black text-blue-700">${stats.totalCost.toLocaleString()}</p>
                </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col justify-center">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Items Tracked</p>
                <p className="text-2xl font-black text-gray-900">{stats.totalItems}</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex flex-col justify-center">
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-1">Confirmed</p>
                <p className="text-2xl font-black text-emerald-700">{stats.confirmed}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 flex flex-col justify-center">
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">Pending Delivery</p>
                <p className="text-2xl font-black text-amber-700">{rentals.filter(r => r.status === 'Confirmed').length}</p>
            </div>
        </div>
      </div>

      {/* Add Rental Item Form */}
      <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
        <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Add Rental Item</h3>
        <form onSubmit={handleAddItem} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <input
            type="text"
            required
            placeholder="Item Name (e.g. Cross-back Chairs)"
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
            className="lg:col-span-2 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <select
            value={newItem.category}
            onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
            className="p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
          >
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            type="number"
            placeholder="Qty"
            value={newItem.quantity}
            onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
            className="p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <select
            value={newItem.vendor}
            onChange={(e) => setNewItem({ ...newItem, vendor: e.target.value })}
            className="p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Select Vendor...</option>
            {vendors.filter(v => v.category.includes('Rental') || v.category === 'Other' || v.category === 'Venue').map(v => (
                <option key={v.id} value={v.name}>{v.name}</option>
            ))}
          </select>
          <button 
            type="submit"
            disabled={!newItem.name.trim()}
            className="w-full bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-lg font-bold transition shadow-md disabled:bg-gray-300 flex items-center justify-center gap-2"
          >
            <PlannerIcons.Plus /> Add Item
          </button>
        </form>
      </div>

      {/* Rentals Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex-1">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-[10px] uppercase tracking-widest text-gray-500">
                <th className="p-4 font-bold">Item Name</th>
                <th className="p-4 font-bold">Category</th>
                <th className="p-4 font-bold text-center">Qty</th>
                <th className="p-4 font-bold">Vendor</th>
                <th className="p-4 font-bold">Status</th>
                <th className="p-4 font-bold">Cost ($)</th>
                <th className="p-4 font-bold text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rentals.map(item => (
                <tr key={item.id} className="hover:bg-gray-50/50 transition group">
                  <td className="p-4">
                    <input 
                        type="text" 
                        value={item.name} 
                        onChange={(e) => handleUpdateItem(item.id, 'name', e.target.value)}
                        className="bg-transparent border-none outline-none w-full text-sm font-bold text-gray-900 focus:ring-1 focus:ring-blue-500 rounded px-1"
                    />
                  </td>
                  <td className="p-4">
                    <select
                        value={item.category}
                        onChange={(e) => handleUpdateItem(item.id, 'category', e.target.value)}
                        className="bg-transparent border-none outline-none text-xs font-semibold text-gray-600 cursor-pointer"
                    >
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="p-4 text-center">
                    <input 
                        type="number" 
                        value={item.quantity || ''} 
                        onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                        className="bg-gray-50 border border-gray-200 rounded w-16 text-center text-sm font-bold p-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </td>
                  <td className="p-4">
                    <input 
                        type="text" 
                        value={item.vendor || ''} 
                        onChange={(e) => handleUpdateItem(item.id, 'vendor', e.target.value)}
                        placeholder="Assign vendor..."
                        className="bg-transparent border-none outline-none w-full text-sm text-gray-600 focus:ring-1 focus:ring-blue-500 rounded px-1"
                    />
                  </td>
                  <td className="p-4">
                    <select
                        value={item.status}
                        onChange={(e) => handleUpdateItem(item.id, 'status', e.target.value)}
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md outline-none cursor-pointer appearance-none border ${
                            item.status === 'Delivered' || item.status === 'Returned' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            item.status === 'Confirmed' || item.status === 'Reserved' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-gray-100 text-gray-600 border-gray-200'
                        }`}
                    >
                        {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-4">
                    <div className="relative">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                        <input 
                            type="number" 
                            value={item.cost || ''} 
                            onChange={(e) => handleUpdateItem(item.id, 'cost', e.target.value)}
                            placeholder="0.00"
                            className="bg-transparent border-none outline-none w-full text-sm pl-3 font-medium focus:ring-1 focus:ring-blue-500 rounded"
                        />
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100"
                      title="Delete Item"
                    >
                      <PlannerIcons.Trash />
                    </button>
                  </td>
                </tr>
              ))}
              {rentals.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-12 text-center text-gray-400">
                    <div className="text-4xl mb-3 opacity-50"><PlannerIcons.Box /></div>
                    <p className="font-medium">No rentals tracked yet.</p>
                    <p className="text-sm mt-1">Add items like chairs, linens, or lighting to manage your inventory.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}